import React from 'react';

import {
  Breakpoints,
  Card,
  CTA,
  CustomProgress,
  Divider,
  Edit,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import { AppConstants } from '../../utils/app-constants';
import {
  calculateServiceProgressFromSchedule,
  formatNumberWithCommas,
  parseMileageNumber,
} from '../../utils/general';
import { CustomNextSvg } from '../../icons/CustomNextSvg';
import { CustomServiceSvg } from '../../icons/CustomServiceSvg';
import {
  StyledCardBody,
  StyledCardContainer,
  StyledCardHeader,
  StyledHeaderLeftSection,
  StyledHeaderRightSection,
  StyledMileageNextContainer,
  StyledMileagePreviousContainer,
  StyledMileageProgressContainer,
  StyledNextServiceCardContainer,
} from './styled';
import { onVWBasicLinkClickEvent } from '../../tagging/tagging-helpers';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import { MaintenanceSchedule } from '../../typings/general';

export const NextServiceCard = ({
  cardsAmount,
  setMaintenanceSchedule,
  maintenanceScheduleData,
}: {
  cardsAmount: number;
  setMaintenanceSchedule: (value: any) => void;
  maintenanceScheduleData?: MaintenanceSchedule;
}) => {
  const trackingManager = useTrackingManager();
  const { mode } = useFeatureAppConfig();
  const [store] = useStore();
  const { translationHandler } = useMaintenanceContext();
  const { form } = store;
  const { mileage } = form;
  const formattedMileage: number = parseMileageNumber(mileage) ?? 0;
  const is10KMileage: boolean = formattedMileage <= 10000;
  const isRoundMileage: boolean = formattedMileage % 10000 === 0;

  const {
    isOverdue,
    milesUntilNextService,
    nextServiceMileage,
    previousServiceMileage,
    progress,
  } = calculateServiceProgressFromSchedule(
    formattedMileage,
    maintenanceScheduleData,
  );

  const getTranslationKey: string =
    formattedMileage > 10000 ? 'previous.service.label' : 'new.vehicle.label';

  const handleTracking = () => {
    const link = {
      name: translationHandler('maintenance.schedule.miles.label'),
      url: window.location.href
    }

    onVWBasicLinkClickEvent(
      trackingManager,
      {
        Type: 'Link',
        Name: translationHandler('maintenance.schedule.miles.label'),
        ItemCount: 1,
        ItemPosition: 1,
      },
      'Link',
      undefined, // document data if needed
      undefined, // additional tracking data
      mode,
      link
    );
  };

  const getLastLabel = (): string => {
    const FIRST_SERVICE = 'maintenance.schedule.first.service.label';
    const CURRENT_SERVICE = 'maintenance.current.service';
    const NEXT_SERVICE = 'maintenance.schedule.next.service.label';

    if (is10KMileage) {
      return FIRST_SERVICE;
    }

    if (cardsAmount === 2) {
      // If we're at the last service mileage
      if (formattedMileage === AppConstants.LAST_SERVICE_MILEAGE) {
        return CURRENT_SERVICE;
      }
      // If we're below the last service mileage
      if (formattedMileage < AppConstants.LAST_SERVICE_MILEAGE) {
        return NEXT_SERVICE;
      }
    }

    if (isRoundMileage) {
      return CURRENT_SERVICE;
    }

    return NEXT_SERVICE;
  };

  return (
    <StyledNextServiceCardContainer>
      <StyledCardContainer cardsAmount={cardsAmount}>
        <Card type="default" data-testid="next-service-card">
          <StyledCardHeader>
            <StyledHeaderLeftSection>
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {translationHandler(
                  'maintenance.schedule.mileage.inputed.label',
                )}
              </TextCustom>
              <CTA
                tag="button"
                emphasis="tertiary"
                onClick={() => {
                  setMaintenanceSchedule(null);
                  handleTracking();
                }}
              >
                {formatNumberWithCommas(parseMileageNumber(mileage) ?? 0) ?? 0}{' '}
                {translationHandler('maintenance.schedule.miles.label')}{' '}
                <Edit variant="small" />
              </CTA>
            </StyledHeaderLeftSection>
            <Divider isVertical />
            <StyledHeaderRightSection>
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {translationHandler(
                  is10KMileage
                    ? 'maintenance.schedule.first.service.is.label'
                    : isOverdue
                      ? 'maintenance.schedule.next.service.is.label'
                      : 'maintenance.schedule.next.service.in.label',
                )}
              </TextCustom>
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {isOverdue ? (
                  translationHandler('maintenance.schedule.due.now.label')
                ) : (
                  <>
                    {milesUntilNextService}{' '}
                    {translationHandler('maintenance.schedule.miles.label')}
                  </>
                )}
              </TextCustom>
            </StyledHeaderRightSection>
          </StyledCardHeader>
          <Divider />
          <StyledCardBody is10KMileage={is10KMileage}>
            <CustomProgress
              endIcon={isOverdue ? <CustomServiceSvg /> : undefined}
              startIcon={isOverdue ? <CustomNextSvg /> : <CustomServiceSvg />}
              value={progress}
            />
            <StyledMileageProgressContainer>
              <StyledMileagePreviousContainer>
                <TextCustom
                  appearance={TextAppearance.copy200}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.bold,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.bold,
                      lineHeight: TextLineHeight.TextLineHeight16,
                    },
                  }}
                >
                  {previousServiceMileage}{' '}
                  {translationHandler('maintenance.schedule.miles.label')}
                </TextCustom>
                <TextCustom
                  appearance={TextAppearance.label100}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize12,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight16,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight16,
                    },
                  }}
                >
                  {translationHandler(
                    `maintenance.schedule.${getTranslationKey}`,
                  )}
                </TextCustom>
              </StyledMileagePreviousContainer>
              <StyledMileageNextContainer>
                <TextCustom
                  appearance={TextAppearance.copy200}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.bold,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.bold,
                      lineHeight: TextLineHeight.TextLineHeight16,
                    },
                  }}
                >
                  {nextServiceMileage}{' '}
                  {translationHandler('maintenance.schedule.miles.label')}
                </TextCustom>
                <TextCustom
                  appearance={TextAppearance.label100}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize12,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight16,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight16,
                    },
                  }}
                >
                  {translationHandler(getLastLabel())}
                </TextCustom>
              </StyledMileageNextContainer>
            </StyledMileageProgressContainer>
          </StyledCardBody>
        </Card>
      </StyledCardContainer>
    </StyledNextServiceCardContainer>
  );
};
